import { notify } from "@/components/common/Notification";
import { getAccessToken, getTenantId } from "@/lib/storeManager";
import {
  createAuthGateBlockedError,
  isAuthGateBlockedError,
  isGluceraPublicAuthRoute,
  shouldAllowProtectedApiRequest,
} from "@/lib/authBootstrapGate";
import {
  clearSessionAndRedirectToLogin,
  getStoredRefreshToken,
  refreshAccessToken,
  shouldLogoutAfterRefreshFailure,
} from "@/services/tokenRefresh";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import axios from "axios";

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

declare module "axios" {
  export interface AxiosRequestConfig {
    notifyMessage?: boolean; // custom flag for controlling notifications
    skipAuthGate?: boolean;
  }
  export interface InternalAxiosRequestConfig {
    notifyMessage?: boolean;
    skipAuthGate?: boolean;
  }
}

axiosConfig.interceptors.request.use(config => {
  if (
    !shouldAllowProtectedApiRequest(config.url, {
      skipAuthGate: config.skipAuthGate,
    })
  ) {
    return Promise.reject(createAuthGateBlockedError());
  }

  const token = getAccessToken();
  const tenantId = getTenantId();
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Check if this is an auth endpoint or assessment endpoint
  const isAuthEndpoint = config.url?.includes("/auth/") || 
                        config.url?.includes("Login") || 
                        config.url?.includes("login");
  
  const isAssessmentEndpoint = config.url?.includes("/new-assessment/") ||
                              config.url?.includes("/assessment");

  // Don't add Authorization header for refresh token OR any auth/login endpoint.
  // A stale Bearer token from a previous session must not be sent when the user
  // is (re-)authenticating, otherwise the backend receives conflicting identities.
  const isTokenRefreshRequest = config.url?.includes("/auth/token-refresh/");
  const isLoginRequest =
    config.url?.includes("staticGoogleLogin") ||
    config.url?.includes("/auth/login") ||
    config.url?.includes("/auth/signin") ||
    config.url?.includes("/auth/register") ||
    config.url?.includes("/auth/signup");

  if (
    token &&
    config.headers &&
    !isTokenRefreshRequest &&
    !isLoginRequest
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add tenantId header if available
  // Skip alert for auth endpoints and assessment endpoints (they handle tenantId internally)
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  } else if (!isAuthEndpoint && !isAssessmentEndpoint) {
    // Only alert if not an auth or assessment endpoint
    console.warn("⚠️ No tenantId found for:", config.url);
    // Removed alert to prevent blocking user flow
  }
  
  if (config.headers) {
    config.headers["X-User-Timezone"] = userTimezone;
    // Note: Cache-Control headers removed - they were causing CORS preflight failures
    // These headers on requests trigger OPTIONS preflight which backend was rejecting
  }

  return config;
});

// Helper function to check if URL should hide messages
const shouldHideMessage = (
  url: string | undefined,
  baseURL: string | undefined
): boolean => {
  if (!url) {
    return false;
  }

  try {
    // Decode URL to handle encoded characters
    const decodedUrl = decodeURIComponent(url);
    const fullUrl = baseURL ? `${baseURL}${url}` : url;
    const decodedFullUrl = baseURL ? `${baseURL}${decodedUrl}` : decodedUrl;

    return hidenMessageAPI.some(path => {
      try {
        const decodedPath = decodeURIComponent(path);
        return (
          url.includes(path) ||
          decodedUrl.includes(path) ||
          url.includes(decodedPath) ||
          decodedUrl.includes(decodedPath) ||
          fullUrl.includes(path) ||
          decodedFullUrl.includes(path)
        );
      } catch {
        // If path decoding fails, just check with original path
        return (
          url.includes(path) ||
          decodedUrl.includes(path) ||
          fullUrl.includes(path) ||
          decodedFullUrl.includes(path)
        );
      }
    });
  } catch {
    // If URL decoding fails, check with original URL only
    const fullUrl = baseURL ? `${baseURL}${url}` : url;
    return hidenMessageAPI.some(
      path => url.includes(path) || fullUrl.includes(path)
    );
  }
};

axiosConfig.interceptors.response.use(
  response => {
    const notifyMessage = response.config.notifyMessage ?? true;
    
    // Check if this API should hide success messages (URL-based or config-based)
    if (shouldHideMessage(response.config.url, response.config.baseURL) || !notifyMessage) {
      return response;
    }
    
    // if (
    //   (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
    //   response.data?.success
    // ) {
    //   const message = response.data?.success?.message ?? response.data.message;
    //   if (message) {
    //     notify.success(message);
    //   }
    // }
    return response;
  },
  async error => {
    const originalRequest = error.config;

    if (isAuthGateBlockedError(error)) {
      return Promise.reject(error);
    }

    const onPublicAuthScreen = isGluceraPublicAuthRoute();

    // Check if this API should hide error messages (URL-based or config-based)
    const shouldHideError =
      onPublicAuthScreen ||
      shouldHideMessage(originalRequest?.url, originalRequest?.baseURL) ||
      originalRequest?.notifyMessage === false;

    if (shouldHideError) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      notify.error(
        error.response.data.message ??
          error.response.data.error.message ??
          error.response.data.error
      );
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getStoredRefreshToken() &&
      !onPublicAuthScreen
    ) {
      originalRequest._retry = true;
      console.warn("🔄 401 Error - attempting token refresh for:", originalRequest.url);

      const tokens = await refreshAccessToken();
      
      console.log("🔍 Token refresh result:", {
        hasAccess: !!tokens?.access,
        hasRefresh: !!tokens?.refresh,
        accessLength: tokens?.access?.length,
      });

      if (tokens?.access) {
        console.log("✅ Token refresh successful - updating headers and retrying");
        const newToken = `Bearer ${tokens.access}`;
        
        // Update default headers
        axiosConfig.defaults.headers.common["Authorization"] = newToken;
        
        // Update original request headers
        originalRequest.headers["Authorization"] = newToken;
        originalRequest.headers["X-Tenant-Id"] = getTenantId() || "";
        
        console.log("🔄 Retrying original request with new token...");
        
        try {
          const retryResponse = await axiosConfig(originalRequest);
          console.log("✅ Retry successful!");
          return retryResponse;
        } catch (retryError) {
          // Refresh worked but the retried call still failed — that's an API
          // error, not an auth failure. Don't wipe the session over it.
          console.error("❌ Retry request failed:", retryError);
          return Promise.reject(retryError);
        }
      }

      // Only log out if the server actually rejected the refresh token.
      // Transient failures (offline/5xx/timeout) keep the session alive.
      if (shouldLogoutAfterRefreshFailure()) {
        console.error("❌ Refresh token invalid - redirecting to login");
        clearSessionAndRedirectToLogin();
      } else {
        console.warn("⚠️ Token refresh failed transiently - keeping session, will retry");
      }
      return Promise.reject(error);
    } else {
      // Check if this API should hide error messages (URL-based or config-based)
      const shouldHideError = shouldHideMessage(originalRequest.url, originalRequest.baseURL) || 
                             originalRequest.notifyMessage === false;
      
      if (!shouldHideError) {
        notify.error(
          error.response?.data?.error?.message ??
            error.response?.data?.message ??
            error.response?.data?.error
        );
      }
      return Promise.reject(error);
    }
  }
);

